// src/PlanPreview.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaSpinner, FaLock, FaPlay, FaArrowLeft } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

export default function PlanPreview() {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location?.state || {};
  const form = state?.form || {};
  const campaignId = state?.campaignId ?? null;
  const campaignKey = state?.campaignKey ?? null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [videoModal, setVideoModal] = useState({ visible: false, url: '', title: '' });

  const colors = useMemo(
    () => ({
      bg: '#070B14',
      bg2: '#08101C',
      bg3: '#0A1224',
      card: '#1a1d2e',
      border: '#273043',
      text: '#ffffff',
      sub: '#cbd5e1',
      muted: '#94a3b8',
      overlay: 'rgba(5,8,16,0.68)',
    }),
    []
  );

  const buildPayload = () => ({
    RequestedPlan: Number(form?.requestedPlan),
    PlanType: 1,
    Price: Number(form?.price ?? 0),

    Age: parseInt(String(form?.age || '').replace(/\D/g, ''), 10) || 0,
    Height: parseInt(String(form?.height || '').replace(/\D/g, ''), 10) || 0,
    Weight: parseInt(String(form?.weight || '').replace(/\D/g, ''), 10) || 0,

    Gender: parseInt(form?.gender || 0, 10),
    Goal: parseInt(form?.goal || 0, 10),
    FitnessExperience: parseInt(form?.fitnessExperience || 0, 10),
    TrainingDaysPerWeek: Number(form?.frequency || 0),

    ActivityLevel: form?.activityLevel === '' || form?.activityLevel == null ? null : Number(form.activityLevel),
    Allergies: form?.allergies === '' || form?.allergies == null ? null : Number(form.allergies),
    MedicalConditions: form?.medicalConditions === '' || form?.medicalConditions == null ? null : Number(form.medicalConditions),
    EquipmentAccess: form?.equipment === '' || form?.equipment == null ? null : Number(form.equipment),
    BodyFocus: form?.bodyFocus === '' || form?.bodyFocus == null ? null : Number(form.bodyFocus),

    Waist: toNullableNumber(form?.measurements?.waist),
    Arm: toNullableNumber(form?.measurements?.arm),
    Thigh: toNullableNumber(form?.measurements?.thigh),
    Chest: toNullableNumber(form?.measurements?.chest),
    Hip: toNullableNumber(form?.measurements?.hip),

    CampaignId: campaignId,
  });

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError('');

      const res = await fetch(`${API_URL}/api/PlanPreview/create-android`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data?.Message || data?.message || 'دریافت نمونه برنامه ناموفق بود.');
        return;
      }

      setPreview(data);
    } catch (e) {
      setError(e?.message || 'خطا در دریافت نمونه برنامه');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!form?.requestedPlan) {
      setError('اطلاعات برنامه پیدا نشد. لطفاً دوباره مراحل ساخت برنامه را انجام دهید.');
      setLoading(false);
      return;
    }

  loadPreview();
}, []);

  const handlePrimaryAction = async () => {
    if (preview?.cta?.primaryAction !== 'purchase_full_plan') return;

    const token = localStorage.getItem('token');

    if (!token) {
      localStorage.setItem(
        'redirectAfterLogin',
        JSON.stringify({
          to: '/plan-preview',
          state: { form, campaignId, campaignKey },
        })
      );
      navigate('/login');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/plan/create-android`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data?.Message || data?.message || 'خطا در ثبت درخواست برنامه');
        return;
      }

      navigate('/payment', {
        state: {
          planId: data?.planId,
          finalPrice: data?.price ?? preview?.priceSummary?.clientPrice ?? Number(form?.price ?? 0),
          planTitle:
            data?.title ||
            preview?.priceSummary?.purchaseTitle ||
            getRequestedPlanLabel(form?.requestedPlan) ||
            'برنامه کامل',
          campaignId,
          campaignKey,
          previewMode: true,
          previewId: preview?.previewId ?? null,
        },
      });
    } catch (e) {
      alert(e?.message || 'مشکلی در ادامه فرایند خرید پیش آمد');
    }
  };

  return (
    <div dir="rtl">
      <div
        className="min-h-screen font-vazir relative overflow-hidden text-white"
        style={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg2}, ${colors.bg3})`,
        }}
      >
        <header className="relative z-20 max-w-6xl mx-auto px-4 pt-4">
          <div
            className="min-h-[64px] rounded-3xl border px-4 flex items-center justify-between backdrop-blur-xl shadow-lg"
            style={{
              backgroundColor: 'rgba(10,15,30,0.80)',
              borderColor: colors.border,
            }}
          >
            <div>
              <div className="font-black text-lg">نمونه برنامه</div>
              <div className="text-xs mt-1" style={{ color: colors.muted }}>
                پیش‌نمایش برنامه شخصی‌سازی‌شده
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-2xl border px-4 py-2 text-sm font-bold"
              style={{ borderColor: colors.border, color: colors.sub }}
            >
              بازگشت
            </button>
          </div>
        </header>

        <main className="relative z-10 max-w-6xl mx-auto px-4 py-6 pb-32">
          {loading ? (
            <LoadingBox />
          ) : error ? (
            <ErrorBox error={error} onRetry={loadPreview} colors={colors} />
          ) : (
            <>
              <PreviewHeroCard colors={colors} hero={preview?.heroSummary} />

              {!!preview?.workoutPreviewDay && (
                <>
                  <SectionTitle title="نمونه برنامه تمرینی" colors={colors} />
                  <WorkoutPreviewCard
                    colors={colors}
                    workout={preview.workoutPreviewDay}
                    onOpenVideo={(url, title) => setVideoModal({ visible: true, url, title })}
                  />
                </>
              )}

              {!!preview?.nutritionPreviewDay && (
                <>
                  <SectionTitle title="نمونه برنامه تغذیه" colors={colors} />
                  <NutritionPreviewCard colors={colors} nutrition={preview.nutritionPreviewDay} />
                </>
              )}

              {!!preview?.lockedSections?.length && (
                <>
                  <SectionTitle title="در نسخه کامل دریافت می‌کنی" colors={colors} />
                  <div className="grid md:grid-cols-2 gap-4">
                    {preview.lockedSections.map((section, idx) => (
                      <LockedSectionCard
                        key={`${section.key}-${idx}`}
                        colors={colors}
                        item={section}
                      />
                    ))}
                  </div>
                </>
              )}

              <PriceSummaryCard
                colors={colors}
                priceSummary={preview?.priceSummary}
                meta={preview?.meta}
                fallbackPrice={Number(form?.price ?? 0)}
              />
            </>
          )}
        </main>

        {!loading && !error && (
          <BottomActionBar
            colors={colors}
            priceSummary={preview?.priceSummary}
            fallbackPrice={Number(form?.price ?? 0)}
            cta={preview?.cta}
            onPrimaryPress={handlePrimaryAction}
            onSecondaryPress={() => navigate(-1)}
          />
        )}

        {videoModal.visible && (
          <VideoModal
            colors={colors}
            videoModal={videoModal}
            onClose={() => setVideoModal({ visible: false, url: '', title: '' })}
          />
        )}
      </div>
    </div>
  );
}

/* UI */

function LoadingBox() {
  return (
    <div className="min-h-[55vh] flex flex-col items-center justify-center">
      <FaSpinner className="animate-spin text-4xl text-blue-500" />
      <div className="mt-4 text-sm text-slate-300">
        در حال آماده‌سازی نمونه برنامه…
      </div>
    </div>
  );
}

function ErrorBox({ error, onRetry, colors }) {
  return (
    <div
      className="rounded-3xl border p-5"
      style={{ backgroundColor: colors.card, borderColor: colors.border }}
    >
      <div className="font-black text-yellow-400 mb-2">خطا در دریافت نمونه برنامه</div>
      <div className="text-sm leading-7" style={{ color: colors.sub }}>
        {error}
      </div>

      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-2xl px-6 py-3 bg-gradient-to-l from-blue-600 to-purple-600 font-black"
      >
        تلاش مجدد
      </button>
    </div>
  );
}

function PreviewHeroCard({ colors, hero }) {
  return (
    <section className="rounded-[28px] p-6 mb-5 bg-gradient-to-l from-blue-600 to-purple-600 shadow-2xl">
      <h1 className="text-2xl md:text-4xl font-black leading-[1.4]">
        {hero?.title || 'نمونه‌ای از برنامه مناسب تو'}
      </h1>

      <p className="mt-3 leading-8 text-blue-50">
        {hero?.subtitle || '-'}
      </p>

      {!!hero?.highlights?.length && (
        <div className="flex flex-wrap gap-2 mt-5">
          {hero.highlights.map((item, idx) => (
            <span key={idx} className="rounded-full bg-white/15 px-3 py-2 text-xs font-bold">
              {item}
            </span>
          ))}
        </div>
      )}

      {!!hero?.warnings?.length && (
        <div className="mt-5 space-y-2 text-yellow-100 text-sm leading-7">
          {hero.warnings.map((item, idx) => (
            <div key={idx}>• {item}</div>
          ))}
        </div>
      )}
    </section>
  );
}

function SectionTitle({ title, colors }) {
  return (
    <h2 className="text-xl md:text-2xl font-black mt-8 mb-4" style={{ color: colors.text }}>
      {title}
    </h2>
  );
}

function WorkoutPreviewCard({ colors, workout, onOpenVideo }) {
  return (
    <section
      className="rounded-[24px] border p-5"
      style={{ backgroundColor: colors.card, borderColor: colors.border }}
    >
      <PreviewInfoRow colors={colors} label="روز" value={workout?.dayLabel} />
      <PreviewInfoRow colors={colors} label="تمرکز" value={workout?.focus} />
      <PreviewInfoRow colors={colors} label="سطح" value={workout?.difficulty} />
      <PreviewInfoRow colors={colors} label="زمان تقریبی" value={workout?.estimatedDuration} />

      {!!workout?.exercises?.length && (
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {workout.exercises.map((item, idx) => (
            <ExerciseCard
              key={`${item.order}-${idx}`}
              colors={colors}
              exercise={item}
              onOpenVideo={onOpenVideo}
            />
          ))}
        </div>
      )}

      {!!workout?.coachNotes?.length && (
        <NoteBox title="یادداشت مربی" notes={workout.coachNotes} colors={colors} type="blue" />
      )}
    </section>
  );
}

function ExerciseCard({ colors, exercise, onOpenVideo }) {
  const mediaUrl = exercise?.videoUrl || exercise?.thumbnailUrl;
  const hasVideo = !!mediaUrl;

  return (
    <div
      className="rounded-2xl border p-4"
      style={{ borderColor: colors.border, backgroundColor: 'rgba(255,255,255,0.02)' }}
    >
      <div className="flex gap-4">
        {hasVideo && (
          <button
            type="button"
            onClick={() => onOpenVideo(mediaUrl, exercise?.name)}
            className="relative w-24 h-24 rounded-2xl overflow-hidden bg-black border shrink-0"
            style={{ borderColor: colors.border }}
          >
            <img
              src={mediaUrl}
              alt={exercise?.name || ''}
              className="w-full h-full object-cover"
            />
            <span className="absolute inset-0 bg-black/35 flex items-center justify-center">
              <FaPlay />
            </span>
          </button>
        )}

        <div className="flex-1 min-w-0">
          <h3 className="font-black mb-2">
            {exercise?.order}. {exercise?.name}
          </h3>

          {exercise?.technique && (
            <p className="text-sm leading-7" style={{ color: colors.sub }}>
              {exercise.technique}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <InfoChip label={`ست: ${exercise?.sets}`} colors={colors} />
        <InfoChip label={`تکرار: ${exercise?.reps}`} colors={colors} />
        <InfoChip label={`استراحت: ${exercise?.rest}`} colors={colors} />
      </div>

      {exercise?.isLockedDetail && (
        <div className="mt-3 rounded-2xl bg-purple-500/10 px-4 py-3 text-sm leading-7" style={{ color: colors.sub }}>
          جزئیات کامل این حرکت در نسخه کامل برنامه باز می‌شود.
        </div>
      )}
    </div>
  );
}

function NutritionPreviewCard({ colors, nutrition }) {
  return (
    <section
      className="rounded-[24px] border p-5"
      style={{ backgroundColor: colors.card, borderColor: colors.border }}
    >
      <PreviewInfoRow colors={colors} label="روز" value={nutrition?.dayLabel} />
      <PreviewInfoRow colors={colors} label="بازه کالری" value={nutrition?.calorieRange} />

      {!!nutrition?.meals?.length && (
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {nutrition.meals.map((meal, idx) => (
            <MealCard key={`${meal.mealType}-${idx}`} colors={colors} meal={meal} />
          ))}
        </div>
      )}

      {!!nutrition?.notes?.length && (
        <NoteBox title="نکات تغذیه‌ای" notes={nutrition.notes} colors={colors} type="green" />
      )}
    </section>
  );
}

function MealCard({ colors, meal }) {
  return (
    <div
      className="rounded-2xl border p-4"
      style={{ borderColor: colors.border, backgroundColor: 'rgba(255,255,255,0.02)' }}
    >
      <h3 className="font-black mb-3">{meal?.mealType}</h3>

      {!!meal?.items?.length &&
        meal.items.map((item, idx) => (
          <div
            key={`${item.name}-${idx}`}
            className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0"
            style={{ borderColor: colors.border }}
          >
            <span>{item.name}</span>
            <span style={{ color: colors.sub }}>{item.amount}</span>
          </div>
        ))}

      {meal?.isLockedAlternative && (
        <div className="mt-3 rounded-2xl bg-purple-500/10 px-4 py-3 text-sm leading-7" style={{ color: colors.sub }}>
          گزینه‌های جایگزین این وعده در نسخه کامل برنامه نمایش داده می‌شود.
        </div>
      )}
    </div>
  );
}

function LockedSectionCard({ colors, item }) {
  return (
    <div
      className="relative rounded-[24px] border p-5 overflow-hidden min-h-[150px]"
      style={{ backgroundColor: colors.card, borderColor: colors.border }}
    >
      <div className="absolute inset-0" style={{ backgroundColor: colors.overlay }} />

      <div className="relative z-10 rounded-2xl bg-slate-950/80 border border-white/10 p-4">
        <div className="flex items-center gap-2 font-black mb-2">
          <FaLock />
          {item?.title}
        </div>

        <p className="text-sm leading-7 text-slate-300">
          {item?.description}
        </p>
      </div>
    </div>
  );
}

function PriceSummaryCard({ colors, priceSummary, meta, fallbackPrice = 0 }) {
  const displayPrice =
    Number(priceSummary?.clientPrice) > 0
      ? Number(priceSummary.clientPrice)
      : Number(fallbackPrice || 0);

  const planLabel =
    priceSummary?.requestedPlanLabel ||
    getRequestedPlanLabel(priceSummary?.requestedPlan) ||
    'برنامه';

  return (
    <section
      className="rounded-[24px] border p-5 mt-6"
      style={{ backgroundColor: colors.card, borderColor: colors.border }}
    >
      <h2 className="font-black mb-4">خلاصه نسخه کامل برنامه</h2>

      <PreviewInfoRow colors={colors} label="نوع برنامه" value={planLabel} />
      <PreviewInfoRow colors={colors} label="هزینه" value={formatTomanFa(displayPrice)} />

      {!!meta?.disclaimer && (
        <div className="mt-4 rounded-2xl bg-yellow-500/10 px-4 py-3 text-sm leading-7" style={{ color: colors.sub }}>
          {meta.disclaimer}
        </div>
      )}
    </section>
  );
}

function BottomActionBar({ colors, priceSummary, fallbackPrice = 0, cta, onPrimaryPress, onSecondaryPress }) {
  const displayPrice =
    Number(priceSummary?.clientPrice) > 0
      ? Number(priceSummary.clientPrice)
      : Number(fallbackPrice || 0);

  return (
    <div className="fixed bottom-4 left-0 right-0 z-40 px-4">
      <div
        className="max-w-5xl mx-auto rounded-[26px] border p-4 shadow-2xl backdrop-blur-xl"
        style={{
          borderColor: colors.border,
          backgroundColor: 'rgba(8,12,22,0.96)',
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="font-black">
              {priceSummary?.purchaseTitle || 'دریافت برنامه کامل'}
            </div>
            <div className="text-xs mt-1 text-slate-300">
              می‌تونی نسخه کامل رو دریافت کنی یا اطلاعاتت رو ویرایش کنی.
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="font-black">{formatTomanFa(displayPrice)}</div>

            <button
              type="button"
              onClick={onPrimaryPress}
              disabled={!cta?.canPurchase}
              className="rounded-2xl px-5 py-3 bg-gradient-to-l from-blue-600 to-purple-600 font-black disabled:opacity-60"
            >
              {cta?.primaryLabel || 'دریافت برنامه کامل'}
            </button>

            <button
              type="button"
              onClick={onSecondaryPress}
              className="rounded-2xl border px-4 py-3 font-bold"
              style={{ borderColor: 'rgba(255,255,255,0.12)' }}
            >
              {cta?.secondaryLabel || 'ویرایش اطلاعات'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoModal({ colors, videoModal, onClose }) {
  const isGif = String(videoModal.url || '').toLowerCase().includes('.gif');
  const isMp4 = String(videoModal.url || '').toLowerCase().includes('.mp4');

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/75 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-3xl p-4"
        style={{ backgroundColor: colors.card }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-black">{videoModal.title || 'نمایش حرکت'}</div>
          <button type="button" onClick={onClose} className="text-xl font-black">×</button>
        </div>

        <div className="w-full h-[70vh] rounded-2xl overflow-hidden bg-black flex items-center justify-center">
          {isMp4 ? (
            <video src={videoModal.url} controls className="w-full h-full object-contain" />
          ) : (
            <img src={videoModal.url} alt={videoModal.title || ''} className="w-full h-full object-contain" />
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewInfoRow({ colors, label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b last:border-b-0" style={{ borderColor: colors.border }}>
      <span style={{ color: colors.sub }}>{label}</span>
      <span className="font-bold text-left">{value || '-'}</span>
    </div>
  );
}

function InfoChip({ colors, label }) {
  return (
    <span
      className="rounded-full border px-3 py-2 text-xs"
      style={{ borderColor: colors.border, color: colors.sub }}
    >
      {label}
    </span>
  );
}

function NoteBox({ title, notes, colors, type }) {
  const bg = type === 'green' ? 'rgba(16,185,129,0.08)' : 'rgba(37,99,235,0.08)';

  return (
    <div className="mt-5 rounded-2xl p-4" style={{ backgroundColor: bg }}>
      <div className="font-black mb-2">{title}</div>
      {notes.map((note, idx) => (
        <div key={idx} className="text-sm leading-7" style={{ color: colors.sub }}>
          • {note}
        </div>
      ))}
    </div>
  );
}

/* helpers */

function toNullableNumber(value) {
  const n = parseInt(String(value || '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function getRequestedPlanLabel(k) {
  switch (Number(k)) {
    case 1:
      return 'برنامه تمرینی';
    case 2:
      return 'برنامه رژیم و تغذیه';
    case 3:
      return 'برنامه تمرین و رژیم غذایی';
    default:
      return 'برنامه';
  }
}

function formatTomanFa(n) {
  return `${Number(n || 0).toLocaleString('fa-IR')} تومان`;
}